/**
 * Approximate HPS ↔ Playtomic skill mapping (reference for support / docs).
 * Signup uses PLAYTOMIC_BAND_OPTIONS + swiss mappings instead of showing this table in the UI.
 */
export const HPS_PLAYTOMIC_SKILL_MAP = [
  { hps: 'HPS Beginner (1.0 – 2.5)', playtomic: 'Playtomic 0.0 – 1.0' },
  { hps: 'HPS Beginner + (3.0 – 3.5)', playtomic: 'Playtomic 1.0 – 2.0' },
  { hps: 'HPS Intermediate (4.0 – 4.5)', playtomic: 'Playtomic 2.0 – 3.0' },
  { hps: 'HPS Intermediate + (5.0 – 5.5)', playtomic: 'Playtomic 3.0 – 4.0' },
  { hps: 'HPS Advanced (6.0 – 6.5)', playtomic: 'Playtomic 4.0 – 5.0' },
  { hps: 'HPS Advanced + (7.0 – 7.5)', playtomic: 'Playtomic 5.0 – 6.0' },
  { hps: 'HPS Elite (8.0 – 10.0)', playtomic: 'Playtomic 6.0+' },
];

/** Internal skill range keys — must stay in sync with server/player expectations. */
export const SKILL_RANGE_OPTIONS = [
  { value: '1.0-2.5', label: '1.0 – 2.5 : Beginner' },
  { value: '3.0-3.5', label: '3.0 – 3.5 : Beginner +' },
  { value: '4.0-4.5', label: '4.0 – 4.5 : Intermediate' },
  { value: '5.0-5.5', label: '5.0 – 5.5 : Intermediate +' },
  { value: '6.0-6.5', label: '6.0 – 6.5 : Advanced' },
  { value: '7.0-7.5', label: '7.0 – 7.5 : Advanced +' },
  { value: '8.0-10.0', label: '8.0 – 10.0 : Elite' },
];

function swissLevelToSkillRangeInner(level) {
  const n = Number(level);
  if (!Number.isFinite(n) || n < 1 || n > 10) return null;
  if (n <= 2) return '1.0-2.5';
  if (n === 3) return '3.0-3.5';
  if (n === 4) return '4.0-4.5';
  if (n === 5) return '5.0-5.5';
  if (n === 6) return '6.0-6.5';
  if (n === 7) return '7.0-7.5';
  return '8.0-10.0';
}

/** Playtomic bands shown at signup (labels via i18n playerRegister.skillRef.playtomic.*). */
export const PLAYTOMIC_BAND_OPTIONS = [
  { id: 'pt_0_1', skillRange: '1.0-2.5', labelKey: 'playerRegister.skillRef.playtomic.pt_0_1' },
  { id: 'pt_1_2', skillRange: '3.0-3.5', labelKey: 'playerRegister.skillRef.playtomic.pt_1_2' },
  { id: 'pt_2_3', skillRange: '4.0-4.5', labelKey: 'playerRegister.skillRef.playtomic.pt_2_3' },
  { id: 'pt_3_4', skillRange: '5.0-5.5', labelKey: 'playerRegister.skillRef.playtomic.pt_3_4' },
  { id: 'pt_4_5', skillRange: '6.0-6.5', labelKey: 'playerRegister.skillRef.playtomic.pt_4_5' },
  { id: 'pt_5_6', skillRange: '7.0-7.5', labelKey: 'playerRegister.skillRef.playtomic.pt_5_6' },
  { id: 'pt_6p', skillRange: '8.0-10.0', labelKey: 'playerRegister.skillRef.playtomic.pt_6p' },
];

/** Swiss Tennis Padel reference levels 1–10 (titles via i18n playerRegister.skillRef.swiss.level_n). */
export const SWISS_PADEL_LEVEL_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => ({
  level,
  skillRange: swissLevelToSkillRangeInner(level),
  labelKey: `playerRegister.skillRef.swiss.level_${level}`,
}));

/** Map Swiss Tennis Padel level (1–10) to internal skillRange key. */
export function swissLevelToSkillRange(level) {
  return swissLevelToSkillRangeInner(level);
}

/** Map Playtomic band option id to internal skillRange key. */
export function playtomicBandToSkillRange(bandId) {
  if (bandId == null || !String(bandId).trim()) return null;
  const row = PLAYTOMIC_BAND_OPTIONS.find((o) => o.id === bandId);
  return row ? row.skillRange : null;
}

/** Full label for a skillRange (Helvetic Padel Series skill level shown after derivation). */
export function skillRangeHpsLabel(skillRange) {
  if (!skillRange) return '';
  const row = SKILL_RANGE_OPTIONS.find((r) => r.value === skillRange);
  return row ? row.label : skillRange;
}

/**
 * Derive internal band from Playtomic or Swiss selection.
 * Signup UI uses mutually exclusive dropdowns (only one source at a time).
 */
export function deriveSkillRangeFromSelections(playtomicBandId, swissLevelStr) {
  const p =
    playtomicBandId != null && String(playtomicBandId).trim()
      ? playtomicBandToSkillRange(String(playtomicBandId).trim())
      : null;
  const raw = swissLevelStr != null && String(swissLevelStr).trim() ? Number(String(swissLevelStr).trim()) : NaN;
  const s = Number.isFinite(raw) && raw >= 1 && raw <= 10 ? swissLevelToSkillRange(raw) : null;

  if (p) return { skillRange: p };
  if (s) return { skillRange: s };
  return { skillRange: null };
}

/** Rows for the skill reference modal: each skill level with matching Playtomic + Swiss levels. */
export function getSkillReferenceTableRows() {
  return SKILL_RANGE_OPTIONS.map((band) => {
    const pt = PLAYTOMIC_BAND_OPTIONS.find((p) => p.skillRange === band.value);
    const swissLevels = SWISS_PADEL_LEVEL_OPTIONS.filter((s) => s.skillRange === band.value).map((s) => s.level);
    return {
      skillRange: band.value,
      playtomicId: pt ? pt.id : null,
      swissLevels,
    };
  });
}

export function exactLevelsForRange(skillRange) {
  if (!skillRange) return [];
  const parts = skillRange.split('-');
  if (parts.length < 2) return [];
  const min = Number(parts[0]);
  const max = Number(parts[1]);
  if (Number.isNaN(min) || Number.isNaN(max)) return [];
  const opts = [];
  for (let i = min; i <= max; i += 0.5) opts.push(i.toFixed(1));
  return opts;
}

/** API canton row: prefer `label` from server when present. */
export function cantonOptionLabel(c) {
  if (!c || c.code == null) return '';
  if (c.label != null && String(c.label).trim()) return String(c.label).trim();
  const code = String(c.code);
  const cons = c.consolidated != null ? String(c.consolidated) : '';
  return code === cons ? code : `${code} (${cons})`;
}
